import React, { useMemo } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '../../components/alert';
import Button from '../../components/button';
import { useMemberships, useOrganization } from '../../contexts/session-context';
import SettingsSurface from './settings-surface';
import ModalHost from './modal-host';
import fastFillSettingsLayout, {
  createEmptyFastFillConfiguration,
} from './fast-fill-settings-layout';
import fastFillTargetFieldsLayout from './fast-fill-target-fields-layout';
import {
  cloneFastFillSettings,
  commitFastFillSettings,
  hasSubmittableFastFillSettings,
  readFastFillEnvironment,
  readFastFillSettings,
  fastFillSettingsAreDirty,
  targetFieldsEnvironmentForConfiguration,
  targetSelectionForConfiguration,
  type FastFillSettingsDraft,
  type FastFillSettingsEnvironment,
  type FastFillTargetSelection,
} from './fast-fill-settings-adapter';

const BILLING_PLANS_URL = '/settings/billing/plans';
const ORGANIZATION_PROFILE_URL = '/settings/organization';
const INFO_BUTTON_HOVER_COLORS = {
  backgroundColor: 'info.dark',
  color: 'common.white',
};
const PHOTO_FASTFILL_BETA_NOTICE_TITLE = 'Photo FastFill is free during the public beta';
const PHOTO_FASTFILL_BETA_NOTICE_DESCRIPTION = 'After the beta ends, an Elite or Enterprise plan will be required to keep using it.';

const contactSalesHref = (organizationId: string, membershipId?: string): string => {
  const body = [
    'Please describe your request:',
    '',
    '',
    '',
    `Organization ID: ${organizationId}`,
    ...(membershipId ? [`User ID: ${membershipId}`] : []),
  ].join('\n');

  return `mailto:sales@fulcrumapp.com?subject=${encodeURIComponent('Request to upgrade')}&body=${encodeURIComponent(body)}`;
};

interface FastFillSettingsPlanProps {
  readonly photoPlanEnabled: boolean;
}

interface FastFillSettingsOrganizationNoticeProps {
  readonly canUpdateOrganization: boolean;
}

function FastFillSettingsPlanNotice({ photoPlanEnabled }: FastFillSettingsPlanProps) {
  if (photoPlanEnabled) return null;

  return (
    <Stack sx={{ mb: 2 }}>
      <Alert
        description={PHOTO_FASTFILL_BETA_NOTICE_DESCRIPTION}
        severity="info"
        sx={{ '& .MuiAlertTitle-root': { fontWeight: 'fontWeightBold' } }}
        title={PHOTO_FASTFILL_BETA_NOTICE_TITLE}
      />
    </Stack>
  );
}

function FastFillSettingsOrganizationNotice({ canUpdateOrganization }: FastFillSettingsOrganizationNoticeProps) {
  return (
    <Stack sx={{ mb: 2 }}>
      <Alert
        action={canUpdateOrganization ? (
          <Button
            color="info"
            href={ORGANIZATION_PROFILE_URL}
            size="medium"
            sx={{ '&:hover': { backgroundColor: 'transparent' } }}
            variant="text"
          >
            GO TO ORG PROFILE
          </Button>
        ) : undefined}
        description={canUpdateOrganization
          ? 'Turn it back on in Organization Profile to let members use FastFill.'
          : 'Contact your org admin to turn it back on.'}
        severity="info"
        sx={{ '& .MuiAlertTitle-root': { fontWeight: 'fontWeightBold' } }}
        title="FastFill is turned off for your organization"
      />
    </Stack>
  );
}

interface FastFillSettingsAudioPlanNoticeProps extends FastFillSettingsPlanProps {
  readonly organizationId: string;
  readonly membershipId?: string;
  readonly canManageSubscription: boolean;
}

function FastFillSettingsAudioPlanNotice({
  photoPlanEnabled,
  organizationId,
  membershipId,
  canManageSubscription,
}: FastFillSettingsAudioPlanNoticeProps) {
  if (photoPlanEnabled) return null;

  return (
    <Alert
      severity="info"
      sx={{
        mt: 2,
        '& .MuiAlert-icon': { alignItems: 'center' },
        '& .MuiAlert-message': { flex: 1 },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Typography variant="body2" sx={{ flex: 1 }}>
          Audio FastFill is only available on Elite and Enterprise plans.
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Button
            color="info"
            href={contactSalesHref(organizationId, membershipId)}
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'info.dark',
                textDecoration: 'none',
              },
            }}
            variant="text"
          >
            Contact Sales
          </Button>
          {canManageSubscription && (
            <Button
              color="info"
              href={BILLING_PLANS_URL}
              size="small"
              sx={{ '&:hover': INFO_BUTTON_HOVER_COLORS }}
              variant="contained"
            >
              Upgrade Now
            </Button>
          )}
        </Stack>
      </Stack>
    </Alert>
  );
}

interface FastFillSettingsProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

interface FastFillTargetFieldsContext {
  readonly draft: FastFillSettingsDraft;
  readonly rowId?: string;
}

const fastFillTargetSelectionIsDirty = (
  initial: FastFillTargetSelection,
  draft: FastFillTargetSelection,
): boolean => (
  initial.configurationId !== draft.configurationId
  || initial.selectedTargetFieldKeys.length !== draft.selectedTargetFieldKeys.length
  || initial.selectedTargetFieldKeys.some(
    (value, index) => value !== draft.selectedTargetFieldKeys[index],
  )
  || (initial.selectedLegacyTargetDataNames ?? []).length
    !== (draft.selectedLegacyTargetDataNames ?? []).length
  || (initial.selectedLegacyTargetDataNames ?? []).some(
    (value, index) => value !== draft.selectedLegacyTargetDataNames?.[index],
  )
);

const sourceFieldForTargetFields = (
  { draft, rowId }: FastFillTargetFieldsContext,
  environment: FastFillSettingsEnvironment,
) => targetFieldsEnvironmentForConfiguration(draft, environment, rowId).sourceField;

export default function FastFillSettings({
  open,
  onClose,
}: FastFillSettingsProps) {
  const organization = useOrganization();
  const currentMembership = useMemberships().find(({ id }) => id === organization.id);
  const initialData = useMemo(() => {
    const settings = readFastFillSettings();

    return !open || settings.configurations.length > 0
      ? settings
      : {
        ...settings,
        configurations: [createEmptyFastFillConfiguration()],
      };
  }, [open]);
  const formEnvironment = useMemo(readFastFillEnvironment, [open]);
  const environment = useMemo(() => ({
    ...formEnvironment,
    fastfillEnabled: organization.fastfillEnabled,
    photoPlanEnabled: organization.fastfillPhotoPlanEnabled,
  }), [formEnvironment, organization.fastfillEnabled, organization.fastfillPhotoPlanEnabled]);
  const child = useMemo(() => ({
    title: (context: FastFillTargetFieldsContext) => {
      const sourceField = sourceFieldForTargetFields(context, environment);

      return sourceField ? `Select Target Fields for ${sourceField.label}` : 'Select Target Fields';
    },
    subtitle: 'Choose which fields should be filled by Photo FastFill.',
    getInitialData: ({ draft, rowId }: FastFillTargetFieldsContext) => (
      targetSelectionForConfiguration(draft, rowId)
    ),
    cloneDraft: (draft: FastFillTargetSelection): FastFillTargetSelection => ({
      configurationId: draft.configurationId,
      selectedTargetFieldKeys: [...draft.selectedTargetFieldKeys],
      ...(draft.selectedLegacyTargetDataNames
        ? { selectedLegacyTargetDataNames: [...draft.selectedLegacyTargetDataNames] }
        : {}),
      ...(draft.legacyPromptOrder
        ? { legacyPromptOrder: draft.legacyPromptOrder.map((target) => ({ ...target })) }
        : {}),
    }),
    environment: ({ draft, rowId }: FastFillTargetFieldsContext) => (
      targetFieldsEnvironmentForConfiguration(draft, environment, rowId)
    ),
    layout: fastFillTargetFieldsLayout,
    host: ModalHost,
    submitLabel: 'Apply',
    cancelLabel: 'Cancel',
    hideCloseButton: true,
    paperSx: (theme) => ({
      overflow: 'hidden',
      width: { md: theme.spacing(78.75) },
      '& .MuiDialogTitle-root': {
        overflow: 'hidden',
        padding: theme.spacing(2, 3, 0.5),
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      '& .MuiDialogContent-root': { overflow: 'hidden', padding: 0 },
      '& .MuiDialogActions-root': { padding: theme.spacing(1, 3) },
    }),
    submitDisabled: (initial: FastFillTargetSelection, draft: FastFillTargetSelection) => (
      !fastFillTargetSelectionIsDirty(initial, draft)
    ),
    isDirty: fastFillTargetSelectionIsDirty,
    confirmDiscard: true,
    ignoreBackdropClick: false,
    apply: (
      draft: FastFillSettingsDraft,
      selection: FastFillTargetSelection,
      { rowId }: { rowId?: string },
    ): FastFillSettingsDraft => {
      if (!rowId) return draft;

      const hasConfiguration = draft.configurations.some(({ id }) => id === rowId);
      if (!hasConfiguration) {
        return draft;
      }

      return {
        ...draft,
        configurations: draft.configurations.map((currentConfiguration) => (
          currentConfiguration.id === rowId
            ? {
              ...currentConfiguration,
              targetFieldKeys: [...selection.selectedTargetFieldKeys],
              legacyTargetDataNames: [...(selection.selectedLegacyTargetDataNames ?? [])],
              legacyPromptOrder: selection.legacyPromptOrder?.map((target) => ({ ...target })) ?? [],
            }
            : currentConfiguration
        )),
      };
    },
  }), [environment]);

  return (
    <SettingsSurface
      open={open}
      title="FastFill Settings"
      contentBeforeLayout={organization.fastfillEnabled ? (
        <FastFillSettingsPlanNotice photoPlanEnabled={environment.photoPlanEnabled} />
      ) : (
        <FastFillSettingsOrganizationNotice
          canUpdateOrganization={environment.canUpdateOrganization}
        />
      )}
      contentAfterLayout={organization.fastfillEnabled ? (
        <FastFillSettingsAudioPlanNotice
          photoPlanEnabled={environment.photoPlanEnabled}
          organizationId={organization.id}
          membershipId={currentMembership?.membershipId}
          canManageSubscription={environment.canManageSubscription}
        />
      ) : null}
      initialData={initialData}
      targetIdentity={window.FormBuilder?.model}
      cloneDraft={cloneFastFillSettings}
      environment={environment}
      layout={fastFillSettingsLayout}
      host={ModalHost}
      submitLabel="Save"
      cancelLabel="Cancel"
      formSx={{ margin: 0 }}
      submitDisabled={(draft) => (
        !environment.fastfillEnabled
        || !fastFillSettingsAreDirty(initialData, draft)
        || !hasSubmittableFastFillSettings(initialData, draft)
      )}
      child={child}
      isDirty={fastFillSettingsAreDirty}
      onSubmit={commitFastFillSettings}
      onClose={onClose}
    />
  );
}
